import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminDownloadPaperUrl,
  adminGetPaper,
  adminOpenPaperPdf,
  adminPublishPaper,
  adminPublishPreview,
  type ConferencePaper,
} from "../api";
import { PageHeader } from "../shells/PageHeader";

export function AdminPublishPaperPage() {
  const { paperId } = useParams<{ paperId: string }>();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [paper, setPaper] = useState<ConferencePaper | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [titleAlt, setTitleAlt] = useState("");
  const [authors, setAuthors] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [abstract, setAbstract] = useState("");
  const [keywords, setKeywords] = useState("");
  const [acknowledgements, setAcknowledgements] = useState("");
  const [doi, setDoi] = useState("");
  const [isbn, setIsbn] = useState("");
  const [license, setLicense] = useState("");
  const [includeLogo, setIncludeLogo] = useState(true);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  useEffect(() => {
    if (!paperId) return;
    setLoading(true);
    adminGetPaper(paperId)
      .then((p) => {
        setPaper(p);
        setTitle(p.title || "");
        setTitleAlt(p.title_alt || "");
        setAuthors(p.authors || "");
        setAffiliation(p.affiliation || "");
        setAbstract(p.abstract || "");
        setKeywords(p.keywords || "");
        setAcknowledgements(p.acknowledgements || "");
        setDoi(p.doi || "");
        setIsbn(p.isbn || "");
        setLicense(p.license || "");
      })
      .catch((e) => setError(e instanceof Error ? e.message : t.common.error))
      .finally(() => setLoading(false));
  }, [paperId, t.common.error]);

  const buildForm = () => {
    const fd = new FormData();
    fd.set("title", title);
    fd.set("title_alt", titleAlt);
    fd.set("authors", authors);
    fd.set("affiliation", affiliation);
    fd.set("abstract", abstract);
    fd.set("keywords", keywords);
    fd.set("acknowledgements", acknowledgements);
    fd.set("doi", doi);
    fd.set("isbn", isbn);
    fd.set("license", license);
    fd.set("include_logo", includeLogo ? "true" : "false");
    if (pdfFile) fd.set("pdf", pdfFile);
    return fd;
  };

  const onPreview = async () => {
    if (!paperId) return;
    setError(null);
    setMessage(null);
    try {
      const blob = await adminPublishPreview(paperId, buildForm());
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    }
  };

  const onPublish = async (e: FormEvent) => {
    e.preventDefault();
    if (!paperId) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await adminPublishPaper(paperId, buildForm());
      setPaper(updated);
      setMessage(t.admin.publishSuccess);
      setTimeout(() => navigate("/admin/papers"), 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setSaving(false);
    }
  };

  const onDownloadWord = async () => {
    if (!paperId) return;
    try {
      const { download_url } = await adminDownloadPaperUrl(paperId);
      window.open(download_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    }
  };

  if (loading) return <p className="dn-muted">{t.common.loading}</p>;
  if (!paper) {
    return (
      <div>
        <PageHeader title={t.admin.publishTitle} backTo="/admin/papers" backLabel={t.admin.papers} />
        <div className="dn-alert dn-alert-error">{error || t.common.error}</div>
      </div>
    );
  }

  const canPublish = paper.status === "accepted" || paper.status === "published";

  return (
    <div>
      <PageHeader
        title={t.admin.publishTitle}
        lead={t.admin.publishLead}
        backTo="/admin/papers"
        backLabel={t.admin.papers}
      />

      <div className="dn-card" style={{ marginBottom: "1rem" }}>
        <p>
          <strong>{t.admin.currentStatus}:</strong>{" "}
          {paper.status === "published" || (paper.status === "accepted" && paper.is_public)
            ? t.status.publishedAccepted
            : paper.status === "accepted"
              ? t.status.accepted
              : paper.status}
        </p>
        <p className="dn-muted">
          {t.admin.manuscript}: {paper.original_filename || "—"}
        </p>
        <div className="dn-cta-row">
          {paper.original_filename && (
            <button type="button" className="dn-btn dn-btn-ghost" onClick={onDownloadWord}>
              {t.admin.downloadWord}
            </button>
          )}
          {paper.has_pdf && (
            <button
              type="button"
              className="dn-btn dn-btn-ghost"
              onClick={async () => {
                const blob = await adminOpenPaperPdf(paper.id);
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank", "noopener,noreferrer");
                window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
              }}
            >
              {t.admin.viewPdf}
            </button>
          )}
        </div>
      </div>

      {!canPublish && (
        <div className="dn-alert dn-alert-error">{t.admin.publishNeedAccept}</div>
      )}

      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>{message}</div>}

      <form className="dn-card dn-publish-form" onSubmit={onPublish}>
        <label>
          {t.admin.fieldTitle}
          <input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <label>
          {t.admin.fieldTitleAlt}
          <input value={titleAlt} onChange={(e) => setTitleAlt(e.target.value)} />
        </label>
        <label>
          {t.admin.fieldAuthors}
          <input value={authors} onChange={(e) => setAuthors(e.target.value)} required />
        </label>
        <label>
          {t.admin.fieldAffiliation}
          <input value={affiliation} onChange={(e) => setAffiliation(e.target.value)} required />
        </label>
        <label>
          {t.admin.fieldAbstract}
          <textarea value={abstract} onChange={(e) => setAbstract(e.target.value)} rows={5} />
        </label>
        <label>
          {t.admin.fieldKeywords}
          <input value={keywords} onChange={(e) => setKeywords(e.target.value)} />
        </label>
        <label>
          {t.admin.fieldAcknowledgements}
          <textarea
            value={acknowledgements}
            onChange={(e) => setAcknowledgements(e.target.value)}
            rows={4}
          />
        </label>
        <div className="dn-publish-grid">
          <label>
            DOI
            <input value={doi} onChange={(e) => setDoi(e.target.value)} placeholder="10.xxxx/…" />
          </label>
          <label>
            ISBN
            <input value={isbn} onChange={(e) => setIsbn(e.target.value)} />
          </label>
          <label>
            {t.admin.fieldLicense}
            <input value={license} onChange={(e) => setLicense(e.target.value)} placeholder="CC BY 4.0" />
          </label>
        </div>

        <label className="dn-publish-check">
          <input
            type="checkbox"
            checked={includeLogo}
            onChange={(e) => setIncludeLogo(e.target.checked)}
          />
          {t.admin.includeLogo}
        </label>

        <label>
          {t.admin.uploadCameraReady}
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
          />
          <span className="dn-muted">{t.admin.uploadCameraReadyHelp}</span>
        </label>

        <div className="dn-cta-row" style={{ marginTop: "1rem" }}>
          <button
            type="button"
            className="dn-btn dn-btn-ghost"
            onClick={onPreview}
            disabled={!canPublish || saving}
          >
            {t.admin.previewPdf}
          </button>
          <button type="submit" className="dn-btn dn-btn-primary" disabled={!canPublish || saving}>
            {saving ? t.common.loading : t.admin.publishNow}
          </button>
        </div>
      </form>
    </div>
  );
}
